import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Radio,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CreditCardInput from 'react-credit-card-input';
import creditCardType from 'credit-card-type';
import payment from 'payment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import isExpiryInvalid from '../../utils/isExpiryInvalid';
import { buildEncryptedCardPayload } from '../../services/paymentCrypto';
import { establishCardKey, getPaymentMethods, payOrder, tokenizeAndUpdateOrders, updateOrderCard, updateOrdersPaymentMethod } from '../../services/orderBilling';

const validationSchema = Yup.object({
    nameOnCard: Yup.string().required('Name on card is required'),
    address1: Yup.string().required('Address line 1 is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    postalCode: Yup.string()
        .required('Postal code is required')
        .matches(/^\d{5}$/, 'Must be a 5-digit number'),
});

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount || 0);

const displayName = (order) => order.businessName || order.businessAccountName || order.accountName;

const getFullYear = (input) => {
    const currentYearPrefix = new Date().getFullYear().toString().slice(0, 2);
    return parseInt(currentYearPrefix + input, 10).toString();
};

// Card dialog shared by "Update card" and "Pay now". In `pay` mode it runs a server-side
// Chargent Sale on a single order; in `update` mode it sets the default card on the group.
// Either way the customer can pick a saved card or enter a new one (and if there are no saved
// cards it drops straight to the new-card form).
function UpdatePaymentDialog({ open, onClose, orders, contactId, onToast, onUpdated, mode = 'update' }) {
    const theme = useTheme();
    const isPay = mode === 'pay';
    const orderIds = useMemo(() => (orders || []).map((o) => o.id), [orders]);
    const payAmount = isPay ? orders?.[0]?.chargeAmount : null;

    const [paymentMode, setPaymentMode] = useState('existing');
    const [methods, setMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [selectedMethodId, setSelectedMethodId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [cardError, setCardError] = useState('');

    const { values, touched, errors, getFieldProps, setTouched, validateForm, resetForm } = useFormik({
        initialValues: { nameOnCard: '', address1: '', address2: '', city: '', state: '', postalCode: '', country: 'US' },
        validationSchema,
        onSubmit: () => {},
    });
    const fieldError = (name) => (touched[name] && errors[name] ? errors[name] : '');

    useEffect(() => {
        if (!open) {
            return;
        }
        setSelectedMethodId(null);
        setCardNumber('');
        setExpiry('');
        setCvc('');
        setCardError('');
        resetForm();
        setLoadingMethods(true);
        getPaymentMethods()
            .then((result) => {
                const list = result?.isSuccess && Array.isArray(result.data) ? result.data : [];
                setMethods(list);
                setPaymentMode(list.length > 0 ? 'existing' : 'new');
            })
            .catch(() => {
                setMethods([]);
                setPaymentMode('new');
            })
            .finally(() => setLoadingMethods(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const finish = (ok, message) => {
        setSubmitting(false);
        if (onToast) {
            onToast(ok ? 'success' : 'error', message);
        }
        if (ok) {
            if (onUpdated) {
                onUpdated(orderIds, isPay ? 'paid' : 'updated');
            }
            onClose();
        }
    };

    const succeeded = (result) => result?.isSuccess && typeof result.data === 'string' && result.data.startsWith('Success');

    const submitExisting = async () => {
        if (!selectedMethodId) {
            if (onToast) {
                onToast('error', 'Please select a card.');
            }
            return;
        }
        setSubmitting(true);
        try {
            const result = isPay ? await payOrder(orderIds[0], selectedMethodId, null) : await updateOrdersPaymentMethod(orderIds, selectedMethodId);
            finish(succeeded(result), succeeded(result) ? (isPay ? 'Payment successful.' : 'Payment method updated.') : result?.data || (isPay ? 'Payment could not be completed.' : 'Could not update the payment method.'));
        } catch {
            finish(false, isPay ? 'Payment could not be completed.' : 'Could not update the payment method.');
        }
    };

    const submitNewCard = async () => {
        if (!(cardNumber && expiry && cvc)) {
            setCardError('Card details are required');
            return;
        }
        if (!payment.fns.validateCardNumber(cardNumber)) {
            setCardError('Please enter a valid card number');
            return;
        }
        const expErr = isExpiryInvalid(expiry.split(' / ').join('/'));
        if (expErr) {
            setCardError(typeof expErr === 'string' ? expErr : 'Please enter a valid expiration date');
            return;
        }
        if (!payment.fns.validateCardCVC(cvc)) {
            setCardError('Please enter a valid CVC');
            return;
        }
        setCardError('');
        setSubmitting(true);
        try {
            const aesKey = await establishCardKey(contactId);
            if (!aesKey) {
                finish(false, 'Could not start a secure session. Please try again.');
                return;
            }
            const brand = creditCardType(cardNumber.substring(0, 4));
            const cardData = {
                ...values,
                cardType: brand?.[0]?.niceType || '',
                creditCardNumber: cardNumber,
                cardExpirationMonth: expiry.split('/')[0].trim(),
                cardExpirationYear: getFullYear(expiry.split('/')[1].trim()),
                cvv: cvc,
            };
            const payloadData = buildEncryptedCardPayload(cardData, aesKey);
            if (isPay) {
                // 1) One-shot server-side Sale on the new card — charges reliably.
                const paid = await payOrder(orderIds[0], null, payloadData);
                if (!succeeded(paid)) {
                    finish(false, paid?.data || 'Payment could not be completed.');
                    return;
                }
                // 2) Save that card on the order via the portal's proven path (PortalAPIService
                // .updatedContent: tokenize + set as the order's default). The charge already went
                // through, so this never fails the payment — but surface a note if it didn't save
                // (temporary, to diagnose the card-not-updating issue).
                let saveNote = '';
                try {
                    const saved = await updateOrderCard(contactId, payloadData, orderIds[0]);
                    if (!succeeded(saved)) {
                        saveNote = ` (card not saved: ${saved?.data || 'no response'})`;
                    }
                } catch (e) {
                    saveNote = ` (card not saved: ${e?.message || 'request failed'})`;
                }
                finish(true, `Payment successful.${saveNote}`);
            } else {
                const result = await tokenizeAndUpdateOrders(payloadData, orderIds);
                finish(succeeded(result), succeeded(result) ? 'New card saved and applied.' : result?.data || 'Could not save the card.');
            }
        } catch {
            finish(false, isPay ? 'Payment could not be completed.' : 'Could not save the card.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitting) {
            return;
        }
        if (paymentMode === 'existing') {
            submitExisting();
        } else {
            setTouched({ nameOnCard: true, address1: true, city: true, state: true, postalCode: true });
            validateForm().then((errs) => {
                if (Object.keys(errs).length === 0) {
                    submitNewCard();
                }
            });
        }
    };

    const submitLabel = isPay ? `Pay ${formatCurrency(payAmount)}` : orderIds.length > 1 ? `Update ${orderIds.length} orders` : 'Update';

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ component: 'form', onSubmit: handleSubmit }}>
            <DialogTitle>{isPay ? 'Complete payment' : 'Update payment method'}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {isPay
                        ? `You're paying ${formatCurrency(payAmount)} for:`
                        : orderIds.length > 1
                          ? `This card will be used for these ${orderIds.length} orders:`
                          : 'This card will be used for:'}
                </Typography>
                <Box sx={{ mb: 2, pl: 1 }}>
                    {(orders || []).map((o) => (
                        <Typography key={o.id} variant="body2" color="text.secondary">
                            • {displayName(o)} — {o.paymentTitle} ({formatCurrency(o.chargeAmount)})
                        </Typography>
                    ))}
                </Box>

                {methods.length > 0 && (
                    <ToggleButtonGroup exclusive size="small" value={paymentMode} onChange={(e, v) => v && setPaymentMode(v)} sx={{ mb: 2 }}>
                        <ToggleButton value="existing" sx={{ textTransform: 'none' }}>
                            Use a saved card
                        </ToggleButton>
                        <ToggleButton value="new" sx={{ textTransform: 'none' }}>
                            Add a new card
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}

                {loadingMethods ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                    </Box>
                ) : paymentMode === 'existing' ? (
                    <List>
                        {methods.map((m) => (
                            <ListItem key={m.id} disablePadding secondaryAction={<Radio checked={selectedMethodId === m.id} onChange={() => setSelectedMethodId(m.id)} />}>
                                <ListItemButton
                                    selected={selectedMethodId === m.id}
                                    onClick={() => setSelectedMethodId(m.id)}
                                    sx={{ border: '1px solid #EAECF0', borderRadius: '8px', mb: 1, '&.Mui-selected': { border: '1px solid #014DE5' } }}
                                >
                                    <ListItemAvatar>
                                        <PaymentIcon type={m.cardType?.toLowerCase()} format="flatRounded" width={40} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={`${m.cardType || 'Card'} ending in ${m.cardLast4}`}
                                        secondary={<span style={{ color: m.ccExpiringSoon ? theme.palette.error.main : undefined }}>{m.cardExpirationMonth && m.cardExpirationYear ? `Exp. ${m.cardExpirationMonth}/${m.cardExpirationYear}` : 'Exp: Not defined'}</span>}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Box>
                        <FormControl fullWidth error={!!fieldError('nameOnCard')} sx={{ mb: 2 }}>
                            <FormLabel htmlFor="nameOnCard">Name on card *</FormLabel>
                            <TextField id="nameOnCard" placeholder="Enter name" {...getFieldProps('nameOnCard')} error={!!fieldError('nameOnCard')} helperText={fieldError('nameOnCard')} />
                        </FormControl>

                        <FormControl fullWidth error={!!cardError} sx={{ mb: 2 }}>
                            <FormLabel>Card *</FormLabel>
                            <CreditCardInput
                                cardNumberInputProps={{
                                    value: cardNumber,
                                    onChange: (e) => {
                                        setCardNumber(e.target.value);
                                        setCardError('');
                                    },
                                }}
                                cardExpiryInputProps={{
                                    value: expiry,
                                    onChange: (e) => {
                                        setExpiry(e.target.value);
                                        setCardError('');
                                    },
                                }}
                                cardCVCInputProps={{
                                    value: cvc,
                                    onChange: (e) => {
                                        setCvc(e.target.value);
                                        setCardError('');
                                    },
                                }}
                                fieldStyle={{ border: '1px solid gainsboro', borderRadius: '8px' }}
                            />
                            {cardError && <FormHelperText error>{cardError}</FormHelperText>}
                        </FormControl>

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Billing address
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth error={!!fieldError('address1')}>
                                    <FormLabel htmlFor="address1">Address line 1 *</FormLabel>
                                    <TextField id="address1" placeholder="Address line 1" {...getFieldProps('address1')} error={!!fieldError('address1')} helperText={fieldError('address1')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="address2">Address line 2</FormLabel>
                                    <TextField id="address2" placeholder="Address line 2" {...getFieldProps('address2')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth error={!!fieldError('city')}>
                                    <FormLabel htmlFor="city">City *</FormLabel>
                                    <TextField id="city" placeholder="City" {...getFieldProps('city')} error={!!fieldError('city')} helperText={fieldError('city')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth error={!!fieldError('state')}>
                                    <FormLabel htmlFor="state">State *</FormLabel>
                                    <TextField id="state" placeholder="ST" inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }} {...getFieldProps('state')} error={!!fieldError('state')} helperText={fieldError('state')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth error={!!fieldError('postalCode')}>
                                    <FormLabel htmlFor="postalCode">Zip *</FormLabel>
                                    <TextField id="postalCode" placeholder="Zip" {...getFieldProps('postalCode')} error={!!fieldError('postalCode')} helperText={fieldError('postalCode')} />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <LoadingButton type="submit" variant="contained" loading={submitting} disabled={paymentMode === 'existing' && !selectedMethodId} sx={{ textTransform: 'none' }}>
                    {submitLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

export default UpdatePaymentDialog;
